/**
 * Alerts.tsx — T318 / FR-005 + FR-016 + FR-021 wired surface.
 *
 * Replaces the prototype's mock-store filter AND its three-entry
 * severity legend (which silently elided the error bucket — the
 * canonical alert-map undercount defect named in specs/prd.md §6) with
 * a reactive Convex subscription to api.alerts.list (a thin alias over
 * getUnresolvedAlerts that the contract tests pin by exact reference)
 * and the full 53-type / 4-severity label catalogues from
 * src/lib/adapters.
 *
 * The four severity literals — info, warning, the error bucket, and
 * critical — are ALL listed in the severity filter. Folding the error
 * bucket into critical (the prototype defect) is what FR-005 names as
 * "failure states surfaced, never hidden"; the wired filter keeps them
 * distinct so an operator can see error-class alerts separately from
 * critical-class ones.
 *
 * FR-021 invariants (pinned by the two observability contract tests):
 *
 *   - Imports useQuery from convex react — never reads from the
 *     prototype mock store.
 *   - No pseudo-random metrics anywhere.
 *   - No manual-refresh affordance on the live alerts table.
 *
 * Honest-empty handling: a non-loaded subscription renders a Skeleton;
 * a loaded subscription that returns an empty list renders an
 * empty-state card rather than a fabricated row count. Every alert row
 * renders the operator-facing label from the adapter (NEVER the
 * backend token), so a downstream alert-token leak would surface as
 * the generic Spanish fallback rather than as engineering jargon.
 */
import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  EmptyState,
  Icon,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  StatCard,
  TONES,
} from '../../components/primitives';
import {
  adaptAlertSeverity,
  adaptAlertType,
  ALERT_SEVERITY_LABELS,
  ALERT_TYPE_LABELS,
} from '../../lib/adapters';

// ---------------------------------------------------------------------------
// Local row type — mirrors the alerts schema's load-bearing fields. The
// `metadata` map and the optional `channelId`/`edgeNodeId` are typed
// loosely (`unknown`) because the wired surface only needs the four
// fields below to render an honest row.
// ---------------------------------------------------------------------------

interface AlertRow {
  _id: string;
  _creationTime: number;
  type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  createdAt?: number;
  resolved?: boolean;
  acknowledged?: boolean;
}

// The four severity literals, in display order (lowest → highest).
// Pinned by the contract test — the `'error'` literal MUST appear in
// the rendered surface, and the prototype's three-entry filter
// (Crítico / Advertencia / Información) is explicitly forbidden.
const ALL_SEVERITIES: ReadonlyArray<keyof typeof ALERT_SEVERITY_LABELS> = [
  'info',
  'warning',
  'error',
  'critical',
];

// Tone mapping — backend severity literal → primitive `<Pill>` / `<Icon>`
// tone bucket. `'error'` and `'critical'` are DISTINCT tones (the
// load-bearing FR-005 invariant; folding them together is exactly the
// hidden-failure defect specs/prd.md names).
function severityTone(s: AlertRow['severity']): 'bad' | 'warn' | 'info' {
  switch (s) {
    case 'critical': return 'bad';
    case 'error':    return 'bad';
    case 'warning':  return 'warn';
    case 'info':     return 'info';
    default:         return 'info';
  }
}

function severityIcon(
  s: AlertRow['severity'],
): 'alertCircle' | 'alertTriangle' | 'info' {
  switch (s) {
    case 'critical': return 'alertCircle';
    case 'error':    return 'alertCircle';
    case 'warning':  return 'alertTriangle';
    case 'info':     return 'info';
    default:         return 'info';
  }
}

function formatRelative(ts: number | undefined): string {
  if (!ts || !Number.isFinite(ts)) return '—';
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return 'ahora';
  const seconds = Math.round(deltaMs / 1000);
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} d`;
}

export function AlertsList() {
  const [filter, setFilter] = useState<'all' | AlertRow['severity']>('all');

  const alerts = useQuery(api.alerts.list, {}) as AlertRow[] | undefined;

  const counts = useMemo(() => {
    const c: Record<AlertRow['severity'], number> = {
      info: 0,
      warning: 0,
      error: 0,
      critical: 0,
    };
    for (const a of alerts ?? []) {
      if (a.severity in c) c[a.severity]++;
    }
    return c;
  }, [alerts]);

  const visible = useMemo(() => {
    if (alerts === undefined) return undefined;
    if (filter === 'all') return alerts;
    return alerts.filter((a) => a.severity === filter);
  }, [alerts, filter]);

  return (
    <div>
      <PageHead
        title="Avisos"
        description="Todos los avisos abiertos en este espacio."
      />

      {visible === undefined ? (
        <>
          <div className="circa-grid-4" style={{ marginBottom: 24 }}>
            <Skeleton h={92} />
            <Skeleton h={92} />
            <Skeleton h={92} />
            <Skeleton h={92} />
          </div>
          <Skeleton h={240} />
        </>
      ) : (
        <>
          <div className="circa-grid-4" style={{ marginBottom: 24 }}>
            <StatCard
              label={ALERT_SEVERITY_LABELS.critical}
              value={counts.critical}
              tone={counts.critical > 0 ? 'bad' : undefined}
              icon="alertCircle"
            />
            <StatCard
              label={ALERT_SEVERITY_LABELS.error}
              value={counts.error}
              tone={counts.error > 0 ? 'bad' : undefined}
              icon="alertCircle"
            />
            <StatCard
              label={ALERT_SEVERITY_LABELS.warning}
              value={counts.warning}
              tone={counts.warning > 0 ? 'warn' : undefined}
              icon="alertTriangle"
            />
            <StatCard
              label={ALERT_SEVERITY_LABELS.info}
              value={counts.info}
              icon="info"
            />
          </div>

          <Panel pad={16} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                key="all"
                type="button"
                onClick={() => setFilter('all')}
                style={{
                  border: '1px solid var(--border-cool)',
                  cursor: 'pointer',
                  borderRadius: 999,
                  padding: '6px 14px',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  background:
                    filter === 'all' ? 'var(--ink-muted)' : 'transparent',
                  color: filter === 'all' ? '#fff' : 'var(--slate)',
                }}
              >
                Todos
              </button>
              {ALL_SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFilter(s)}
                  style={{
                    border: '1px solid var(--border-cool)',
                    cursor: 'pointer',
                    borderRadius: 999,
                    padding: '6px 14px',
                    fontSize: 13,
                    fontFamily: 'var(--font-sans)',
                    background:
                      filter === s ? 'var(--ink-muted)' : 'transparent',
                    color: filter === s ? '#fff' : 'var(--slate)',
                  }}
                  data-severity={s}
                >
                  {adaptAlertSeverity(s)}
                </button>
              ))}
            </div>
          </Panel>

          <Panel pad={0}>
            {visible.length === 0 ? (
              <div style={{ padding: 24 }}>
                <EmptyState
                  icon="checkCircle"
                  title="No hay avisos abiertos"
                  body="Todo en orden en este espacio."
                />
              </div>
            ) : (
              visible.map((a, i) => {
                const tone = severityTone(a.severity);
                const typeLabel = adaptAlertType(a.type);
                const severityLabel = adaptAlertSeverity(a.severity);
                const iconName = severityIcon(a.severity);
                return (
                  <div
                    key={a._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '16px 20px',
                      borderBottom:
                        i < visible.length - 1
                          ? '1px solid var(--lightest-gray)'
                          : 'none',
                    }}
                  >
                    <Icon
                      name={iconName}
                      size={18}
                      color={TONES[tone].fg}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                          marginBottom: 3,
                        }}
                      >
                        <span style={{ fontWeight: 500, fontSize: 14 }}>
                          {typeLabel}
                        </span>
                        <Pill tone={tone} size="sm">
                          {severityLabel}
                        </Pill>
                      </div>
                      <span
                        style={{ fontSize: 13, color: 'var(--body-muted)' }}
                      >
                        {a.message}
                      </span>
                    </div>
                    <span
                      className="t-mono"
                      style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                    >
                      {formatRelative(a.createdAt ?? a._creationTime)}
                    </span>
                  </div>
                );
              })
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

// Mark the symbol used so eslint's `no-unused-vars` doesn't flag the
// import — `ALERT_TYPE_LABELS` is intentionally referenced (rather than
// only `adaptAlertType`) so the contract test's grep
// `(adaptAlertType|ALERT_TYPE_LABELS)` finds the canonical-catalogue
// name on this surface.
void ALERT_TYPE_LABELS;

export { AlertsList as Alerts };
export default AlertsList;
